import React, { useState, useEffect } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { useNotifications } from '../../contexts/NotificationContext'

interface ScheduledReminder {
  medicationId: string
  medicationName: string
  scheduledTime: string
  reminderTime: Date
  isActive: boolean
}

const ActiveReminders: React.FC = () => {
  const [reminders, setReminders] = useState<ScheduledReminder[]>([])
  const { medications, todayMedications } = useMedications()
  const { settings, scheduleReminder, cancelReminder } = useNotifications()

  useEffect(() => {
    if (settings.enabled && settings.medicationReminders) {
      generateReminders()
    } else {
      setReminders([])
    }
  }, [medications, todayMedications, settings])

  const generateReminders = () => {
    const now = new Date()
    const todayReminders: ScheduledReminder[] = []

    todayMedications.forEach(medication => {
      const times = medication.times || []
      
      if (times.length === 0) {
        // Medication without specific times - create a general reminder for 9 AM
        const generalTime = '09:00'
        const reminderTime = calculateReminderTime(generalTime)
        
        if (reminderTime > now) {
          todayReminders.push({
            medicationId: medication.id,
            medicationName: medication.name,
            scheduledTime: generalTime,
            reminderTime,
            isActive: true
          })
        }
      } else {
        times.forEach((time: string) => {
          const reminderTime = calculateReminderTime(time)
          
          if (reminderTime > now) {
            todayReminders.push({
              medicationId: medication.id,
              medicationName: medication.name,
              scheduledTime: time,
              reminderTime,
              isActive: true
            })
          }
        })
      }
    })

    setReminders(todayReminders.sort((a, b) => a.reminderTime.getTime() - b.reminderTime.getTime()))
  }

  const calculateReminderTime = (scheduledTime: string): Date => {
    const [hours, minutes] = scheduledTime.split(':').map(Number)
    const scheduledDate = new Date()
    scheduledDate.setHours(hours, minutes, 0, 0)
    
    // If scheduled time is in the past, schedule for tomorrow
    if (scheduledDate <= new Date()) {
      scheduledDate.setDate(scheduledDate.getDate() + 1)
    }
    
    // Subtract reminder time
    return new Date(scheduledDate.getTime() - (settings.reminderTimeMinutes * 60 * 1000))
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const formatScheduledTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour24 = parseInt(hours)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const ampm = hour24 >= 12 ? 'PM' : 'AM'
    return `${hour12}:${minutes} ${ampm}`
  }

  const handleToggleReminder = (reminder: ScheduledReminder, enabled: boolean) => {
    if (enabled) {
      scheduleReminder(reminder.medicationName, reminder.scheduledTime)
    } else {
      cancelReminder(reminder.medicationId, reminder.scheduledTime)
    }
    
    setReminders(prev => 
      prev.map(r => 
        r.medicationId === reminder.medicationId && r.scheduledTime === reminder.scheduledTime
          ? { ...r, isActive: enabled }
          : r
      )
    )
  }

  const getTimeUntilReminder = (reminderTime: Date): string => {
    const now = new Date()
    const diffMs = reminderTime.getTime() - now.getTime()
    
    if (diffMs <= 0) return 'Vencido'
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    
    if (diffHours > 0) {
      return `En ${diffHours}h ${diffMinutes}m`
    } else {
      return `En ${diffMinutes}m`
    }
  }

  if (!settings.enabled || !settings.medicationReminders) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4 19h10a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Recordatorios deshabilitados
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            Activa las notificaciones para ver tus recordatorios programados
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Recordatorios Activos
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Próximos recordatorios programados para hoy
        </p>
      </div>

      {reminders.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No hay recordatorios pendientes
          </h4>
          <p className="text-gray-600 dark:text-gray-300">
            Todos tus medicamentos están al día por hoy
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reminders.map((reminder) => (
            <div 
              key={`${reminder.medicationId}-${reminder.scheduledTime}`}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {reminder.medicationName}
                  </h4>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
                    <span>Medicamento: {formatScheduledTime(reminder.scheduledTime)}</span>
                    <span>•</span>
                    <span>Recordatorio: {formatTime(reminder.reminderTime)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {getTimeUntilReminder(reminder.reminderTime)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {settings.reminderTimeMinutes} min antes
                  </div>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminder.isActive}
                    onChange={(e) => handleToggleReminder(reminder, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {reminders.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-300">
              Total de recordatorios hoy:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {reminders.filter(r => r.isActive).length} activos de {reminders.length}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ActiveReminders 